import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import Loader from "Components/Loader";
import NoData from "Components/NoData";
import { SideModalProps } from "DataTypes/Modal";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { RedoxVaccineType } from "DataTypes/Services/Patient.type";
import RedoxFormatter from "Utils/Redox.Formatter";
import DateTime from "Utils/DateTime";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import UpdateVaccine from "./UpdateVaccine";
import RedoxApi from "Service/Redox.api";
import TabPanel from "Components/TabPanel";
import { label } from "Constant/En";

export interface DataActionType {
  data: RedoxVaccineType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const initailState: DataActionType = { data: null, action: null };

const a11yProps: (index: number) => {
  id: string,
  "aria-controls": string
} = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const Vaccine: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const crudPermission = myInfo.roleId != 6;
  const newPatientId = patientInfo.id;
  const [activeVaccineList, setActiveVaccineList] =
    useState<RedoxVaccineType[]>([]);
  const [historicalVaccineList, setHistoricalVaccineList] =
    useState<RedoxVaccineType[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataAction, setDataAction] = useState<DataActionType>(initailState);

  useEffect(() => {
    getVaccineData();
    setDataAction(initailState);
  }, [tabValue]);

  const getVaccineData: () => void = async () => {
    setLoading(true);
    const res: any = await RedoxApi.VaccineSearch({ id1: newPatientId });
    if (res?.success) {
      const formattedList =
        RedoxFormatter.VaccineList(res?.data?.immunizations);
      // TODO: replace with tab-specific APIs once available.
      setActiveVaccineList(formattedList);
      setHistoricalVaccineList([]);
    }
    setLoading(false);
  };

  const handleCloseLayer2: () => void = () => {
    setDataAction({ data: null, action: null })
  };

  const displayVaccineHead: (
    data: RedoxVaccineType
  ) => React.ReactElement = (data) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.productName}
        </Typography>
        {crudPermission && (
          <Box
            display="flex"
            justifyContent={"flex-end"}
            sx={{ "&>div": { ml: 1 } }}
            width="30%"
          >
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "EDIT" })}
            >
              <ImgPath.EditOutlined />
            </Box>
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "DELETE" })}
            >
              <ImgPath.DeleteOutline />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const ActiveUIContent = (
    <Fragment>
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : activeVaccineList.length ? (
          activeVaccineList.map((el) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayVaccineHead(el)}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                    Vaccine Date: {DateTime.ToLocalDate(el.dateTime)}
                  </Typography>
                  <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                    Route: {el.routeName || label.naText}
                  </Typography>
                  <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                    Dose:{" "}
                    {`${(el.doseQuantity + " " + el.doseUnits).trim() ||
                      label.naText}`}
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  const HistoricalUIContent = (
    <Fragment>
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : historicalVaccineList.length ? (
          historicalVaccineList.map((el) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayVaccineHead(el)}
                <Typography variant="subtitle1">
                  Vaccine Date: {DateTime.ToLocalDate(el.dateTime)}
                </Typography>
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Vaccines</Typography>
        <Box display={"flex"}>
          {crudPermission && (
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
              onClick={() => setDataAction({ data: null, action: "ADD" })}
            >
              <ImgPath.PlusIcon titleAccess="Add Vaccine" />
            </Box>
          )}
          <Box
            sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
            onClick={() => {
              handleCloseLayer2();
              onClose();
            }}
          >
            <ImgPath.CloseIcon titleAccess="Close" />
          </Box>
        </Box>
      </Box>
      {/* <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab label="Active" {...a11yProps(0)} />
          <Tab label="Historical" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0} style={{ ...sxTabPanel }}>
        {ActiveUIContent}
      </TabPanel>
      <TabPanel value={tabValue} index={1} style={{ ...sxTabPanel }}>
        {HistoricalUIContent}
      </TabPanel> */}

      {activeVaccineList.length > 0 ? activeVaccineList.map((el) => {
        return (
          <Box key={el.id} sx={{ ...sxContainer }}>
            {displayVaccineHead(el)}
            <Typography variant={"subtitle1"}>
              Dose:{" "}
              {`${(el.doseQuantity + " " + el.doseUnits).trim() ||
                label.naText}`}
            </Typography>
            <Typography variant={"subtitle1"}>
              Administered On: {DateTime.ToLocalDate(el.dateTime)}
            </Typography>
            <Typography variant={"subtitle1"}>
              Administered Via: {el.routeName || label.naText}
            </Typography>
          </Box>
        );
      }) :
        <NoData />
      }

    </Fragment>
  );

  return (
    <Fragment>
      <Box width="40%">{Layer1}</Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box width="58%">
          <UpdateVaccine
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={getVaccineData}
          />
        </Box>
      )}
    </Fragment>
  );
};
export default Vaccine;

const sxHeader = {
  justifyContent: "space-between",
  height: "10%",
  px: 2,
};

const sxTab = {
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

const sxTabPanel = {
  height: "75%",
};

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
  px: 2,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};

const sxMedDetails = {
  width: "32%",
  lineBreak: "anywhere",
};
